import React, { useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Grid,
    TextField, Switch, Button, Alert, CircularProgress,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { useBrand } from "../../../../contexts/BrandContext";

const INITIAL_FORM = { key: "", name: "", min_status_points: 0, rank: 0, active: true };

const CreateTierDialog = ({ open, onClose, onCreated }) => {
    const { currentBrand } = useBrand();
    const brandName = currentBrand?.name || "";
    const [form, setForm] = useState({ ...INITIAL_FORM });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleChange = (field) => (e) => {
        const value = e.target.type === "number" ? Number(e.target.value) : e.target.value;
        setForm((p) => ({ ...p, [field]: value }));
    };

    const isValid = brandName && form.key.trim() && form.name.trim();

    const handleSubmit = async () => {
        if (!isValid) return;
        setSubmitting(true);
        setError(null);
        try {
            const res = await fetch(`${LOYALTY_API_BASE}/admin/loyalty-tiers`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": brandName
                },
                body: JSON.stringify({
                    brand: brandName,
                    key: form.key.trim(),
                    name: form.name.trim(),
                    min_status_points: form.min_status_points,
                    rank: form.rank,
                    active: form.active,
                }),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
            }
            onCreated();
            handleClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => { setForm({ ...INITIAL_FORM }); setError(null); onClose(); };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.tiers.gradient }}>
                        <EmojiEventsIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={600} color="#2c3e50">
                        Nouveau Niveau de Fidélité
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2.5} px={1.5} py={1}
                    sx={{ borderRadius: 2, bgcolor: "#f8f9ff", border: "1px solid #e0e7ff" }}>
                    <StorefrontIcon sx={{ fontSize: 18, color: SECTION_COLORS.tiers.main }} />
                    <Typography fontSize={13} color="text.secondary">Marque :</Typography>
                    <Chip
                        label={brandName || "Aucune marque"}
                        size="small"
                        sx={{
                            fontWeight: 700, fontSize: 12,
                            bgcolor: brandName ? `${SECTION_COLORS.tiers.main}15` : "#fff0f0",
                            color: brandName ? SECTION_COLORS.tiers.main : "#DC2626"
                        }}
                    />
                </Box>

                {!brandName && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                        Veuillez sélectionner une marque avant de créer un niveau.
                    </Alert>
                )}

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth required label="Clé unique" value={form.key}
                            onChange={handleChange("key")} placeholder="ex: gold" size="small"
                            disabled={!brandName} helperText="Identifiant technique unique"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth required label="Nom du niveau" value={form.name}
                            onChange={handleChange("name")} placeholder="ex: Gold" size="small"
                            disabled={!brandName} helperText="Nom affiché aux utilisateurs"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Points minimum" type="number" value={form.min_status_points}
                            onChange={handleChange("min_status_points")} size="small"
                            disabled={!brandName} helperText="Nombre de points requis"
                            InputProps={{ inputProps: { min: 0 } }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Rang" type="number" value={form.rank}
                            onChange={handleChange("rank")} size="small"
                            disabled={!brandName} helperText="Ordre d'affichage (1 = plus bas)"
                            InputProps={{ inputProps: { min: 0 } }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between"
                            px={1.5} py={1} sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600} fontFamily="Montserrat, sans-serif">Activer ce niveau</Typography>
                                <Typography fontSize={11} color="text.secondary">Le niveau sera immédiatement disponible pour les clients</Typography>
                            </Box>
                            <Switch checked={form.active} onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                disabled={!brandName}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.tiers.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.tiers.main }
                                }} />
                        </Box>
                    </Grid>
                </Grid>

                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0" }}>
                <Button onClick={handleClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    sx={{
                        background: SECTION_COLORS.tiers.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3,
                        "&:hover": { boxShadow: `0 4px 16px ${SECTION_COLORS.tiers.main}40` }
                    }}>
                    {submitting ? "Création..." : "Créer le Niveau"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateTierDialog;
